import { useState } from 'react'
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom'
import {
  Squares2X2Icon,
  CubeIcon,
  GiftIcon,
  BuildingStorefrontIcon,
  UsersIcon,
  DocumentTextIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  TagIcon,
  TruckIcon,
  ArchiveBoxIcon,
  ArrowsRightLeftIcon,
  CloudArrowUpIcon,
} from '@heroicons/react/24/outline'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import useAuth from '@hooks/useAuth'
import ShopifyTokenStatus from '@/components/ShopifyTokenStatus'

/**
 * Layout principale dell'applicazione con design moderno.
 * 
 * Features:
 * - Sidebar collassabile con animazioni fluide
 * - Sottomenu espandibili (Catalogo)
 * - Header sticky con menu utente
 * - Navigazione dinamica basata sui permessi
 * - Design responsive
 */
export default function MainLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [catalogoOpen, setCatalogoOpen] = useState(true)
  const [boxOpen, setBoxOpen] = useState(true)
  const [magazzinoOpen, setMagazzinoOpen] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, hasPermission, isAdmin } = useAuth()

  // Verifica se siamo in una pagina del catalogo
  const isCatalogoActive = location.pathname.startsWith('/prodotti') || 
                           location.pathname.startsWith('/categorie') || 
                           location.pathname.startsWith('/fornitori')
  
  // Verifica se siamo in una pagina Box
  const isBoxActive = location.pathname.startsWith('/box')
  
  // Verifica se siamo in una pagina Magazzino
  const isMagazzinoActive = location.pathname.startsWith('/magazzino')
  
  // Verifica se siamo in una pagina Ordini (FASE 7.4)
  const isOrdiniActive = location.pathname.startsWith('/ordini')
  const [ordiniOpen, setOrdiniOpen] = useState(true)

  // Verifica se siamo in una pagina Impostazioni
  const isImpostazioniActive = location.pathname.startsWith('/impostazioni')
  const [impostazioniOpen, setImpostazioniOpen] = useState(isImpostazioniActive)

  // Voci menu principale
  const menuItems = [
    { path: '/', icon: Squares2X2Icon, label: 'Dashboard', show: true },
    { type: 'catalogo' }, // Sottomenu Catalogo
    { type: 'box' }, // Sottomenu Box
    { type: 'magazzino' }, // Sottomenu Magazzino
    { path: '/clienti', icon: UsersIcon, label: 'Clienti', show: true },
    { type: 'ordini' }, // Sottomenu Ordini (FASE 7.4)
    { path: '/integrazioni/shopify', icon: CloudArrowUpIcon, label: 'Shopify', show: isAdmin },
    { path: '/report', icon: ChartBarIcon, label: 'Report', show: hasPermission('view_reports') },
    { type: 'divider' },
    { type: 'impostazioni', show: isAdmin }, // Sottomenu Impostazioni
  ].filter(item => item.show !== false)

  // Sottovoci menu Catalogo
  const catalogoItems = [
    { path: '/prodotti', icon: CubeIcon, label: 'Prodotti' },
    { path: '/categorie', icon: TagIcon, label: 'Categorie' },
    { path: '/fornitori', icon: TruckIcon, label: 'Fornitori' },
  ]

  // Sottovoci menu Box
  const boxItems = [
    { path: '/box/tipologie', icon: GiftIcon, label: 'Tipologie Box' },
    { path: '/box/configurazioni', icon: CubeIcon, label: 'Configurazioni' },
    { path: '/box/componenti', icon: ArchiveBoxIcon, label: 'Componenti' },
  ]

  // Sottovoci menu Magazzino (principali)
  const magazzinoItems = [
    { path: '/magazzino', icon: Squares2X2Icon, label: 'Dashboard' },
    { path: '/magazzino/movimenti', icon: ArrowsRightLeftIcon, label: 'Movimenti' },
  ]

  // Sottovoci menu Ordini (FASE 7.4: Spedizioni Avanzate)
  const ordiniItems = [
    { path: '/ordini', icon: DocumentTextIcon, label: 'Ordini' },
    { path: '/ordini/spedizioni', icon: TruckIcon, label: 'Spedizioni' },
    { path: '/ordini/destinatari', icon: UsersIcon, label: 'Destinatari' },
    { path: '/ordini/colli', icon: ArchiveBoxIcon, label: 'Colli' },
  ]

  // Sottovoci menu Impostazioni
  const impostazioniItems = [
    { path: '/impostazioni/venditori', icon: UsersIcon, label: 'Venditori' },
  ]

  const getInitials = (user) => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
    }
    return user?.username?.[0]?.toUpperCase() || 'U'
  }

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path
    const Icon = item.icon

    return (
      <Link
        to={item.path}
        onClick={() => setMobileOpen(false)}
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent",
          isActive 
            ? "bg-primary text-primary-foreground hover:bg-primary/90" 
            : "text-muted-foreground hover:text-foreground"
        )}
      >
        <Icon className="h-4 w-4" />
        {!collapsed && <span>{item.label}</span>}
      </Link>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Overlay mobile */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-50 h-full bg-card border-r transition-all duration-300 ease-in-out",
        collapsed ? "w-16" : "w-64",
        mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {/* Logo */}
        <div className="flex h-16 items-center justify-between border-b px-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-purple-600">
              <GiftIcon className="h-5 w-5 text-white" />
            </div>
            {!collapsed && (
              <span className="font-semibold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                Box Regalo
              </span>
            )}
          </Link>
          
          {/* Toggle collapse (desktop) */}
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:flex h-8 w-8"
            onClick={() => setCollapsed(!collapsed)}
          >
            <ChevronLeftIcon className={cn(
              "h-4 w-4 transition-transform",
              collapsed && "rotate-180"
            )} />
          </Button>

          {/* Close mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden h-8 w-8"
            onClick={() => setMobileOpen(false)}
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 p-2">
          {menuItems.map((item, index) => {
            if (item.type === 'divider') {
              return <div key={index} className="my-2 h-px bg-border" />
            }
            
            if (item.type === 'catalogo') {
              return (
                <Collapsible
                  key="catalogo"
                  open={catalogoOpen && !collapsed}
                  onOpenChange={setCatalogoOpen}
                >
                  <CollapsibleTrigger asChild>
                    <button
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent",
                        isCatalogoActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={() => !collapsed && setCatalogoOpen(!catalogoOpen)}
                    >
                      <ArchiveBoxIcon className="h-4 w-4" />
                      {!collapsed && (
                        <>
                          <span className="flex-1 text-left">Catalogo</span>
                          <ChevronDownIcon className={cn(
                            "h-4 w-4 transition-transform",
                            catalogoOpen && "rotate-180"
                          )} />
                        </>
                      )}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-1 pt-1">
                    {catalogoItems.map((subItem) => {
                      const isSubActive = location.pathname === subItem.path || 
                                          location.pathname.startsWith(subItem.path + '/')
                      const SubIcon = subItem.icon
                      return (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={() => setMobileOpen(false)}
                          className={cn(
                            "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent ml-4",
                            isSubActive
                              ? "bg-primary text-primary-foreground hover:bg-primary/90"
                              : "text-muted-foreground hover:text-foreground"
                          )}
                        >
                          <SubIcon className="h-4 w-4" />
                          {!collapsed && <span>{subItem.label}</span>}
                        </Link>
                      )
                    })}
                  </CollapsibleContent>
                </Collapsible>
              )
            }

            if (item.type === 'box') {
              return (
                <Collapsible
                  key="box"
                  open={boxOpen && !collapsed}
                  onOpenChange={setBoxOpen}
                >
                  <CollapsibleTrigger asChild>
                    <button
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent",
                        isBoxActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={() => !collapsed && setBoxOpen(!boxOpen)}
                    >
                      <GiftIcon className="h-4 w-4" />
                      {!collapsed && (
                        <>
                          <span className="flex-1 text-left">Box</span>
                          <ChevronDownIcon className={cn(
                            "h-4 w-4 transition-transform",
                            boxOpen && "rotate-180"
                          )} />
                        </>
                      )}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-1 pt-1">
                    {boxItems.map((subItem) => {
                      const isSubActive = location.pathname === subItem.path || 
                                          location.pathname.startsWith(subItem.path + '/')
                      const SubIcon = subItem.icon
                      return (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={() => setMobileOpen(false)}
                          className={cn(
                            "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent ml-4",
                            isSubActive
                              ? "bg-primary text-primary-foreground hover:bg-primary/90"
                              : "text-muted-foreground hover:text-foreground"
                          )}
                        >
                          <SubIcon className="h-4 w-4" />
                          {!collapsed && <span>{subItem.label}</span>}
                        </Link>
                      )
                    })}
                  </CollapsibleContent>
                </Collapsible>
              )
            }

            if (item.type === 'magazzino') {
              return (
                <Collapsible
                  key="magazzino"
                  open={magazzinoOpen && !collapsed}
                  onOpenChange={setMagazzinoOpen}
                >
                  <CollapsibleTrigger asChild>
                    <button
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent",
                        isMagazzinoActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={() => !collapsed && setMagazzinoOpen(!magazzinoOpen)}
                    >
                      <BuildingStorefrontIcon className="h-4 w-4" />
                      {!collapsed && (
                        <>
                          <span className="flex-1 text-left">Magazzino</span>
                          <ChevronDownIcon className={cn(
                            "h-4 w-4 transition-transform",
                            magazzinoOpen && "rotate-180"
                          )} />
                        </>
                      )}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-1 pt-1">
                    {magazzinoItems.map((subItem) => {
                      const isDashboardPath = subItem.path === '/magazzino'
                      const isSubActive = isDashboardPath
                        ? (location.pathname === '/magazzino' || location.pathname === '/magazzino/')
                        : (location.pathname === subItem.path ||
                           location.pathname.startsWith(subItem.path + '/'))
                      const SubIcon = subItem.icon
                      return (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={() => setMobileOpen(false)}
                          className={cn(
                            "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent ml-4",
                            isSubActive
                              ? "bg-primary text-primary-foreground hover:bg-primary/90"
                              : "text-muted-foreground hover:text-foreground"
                          )}
                        >
                          <SubIcon className="h-4 w-4" />
                          {!collapsed && <span>{subItem.label}</span>}
                        </Link>
                      )
                    })}

                  </CollapsibleContent>
                </Collapsible>
              )
            }

            if (item.type === 'ordini') {
              return (
                <Collapsible
                  key="ordini"
                  open={ordiniOpen && !collapsed}
                  onOpenChange={setOrdiniOpen}
                >
                  <CollapsibleTrigger asChild>
                    <button
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent",
                        isOrdiniActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={() => !collapsed && setOrdiniOpen(!ordiniOpen)}
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                      {!collapsed && (
                        <>
                          <span className="flex-1 text-left">Ordini</span>
                          <ChevronDownIcon className={cn(
                            "h-4 w-4 transition-transform",
                            ordiniOpen && "rotate-180"
                          )} />
                        </>
                      )}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-1 pt-1">
                    {ordiniItems.map((subItem) => {
                      const isSubActive = location.pathname === subItem.path || 
                                          (location.pathname.startsWith(subItem.path + '/') && subItem.path !== '/ordini')
                      const SubIcon = subItem.icon
                      return (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={() => setMobileOpen(false)}
                          className={cn(
                            "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent ml-4",
                            isSubActive
                              ? "bg-primary text-primary-foreground hover:bg-primary/90"
                              : "text-muted-foreground hover:text-foreground"
                          )}
                        >
                          <SubIcon className="h-4 w-4" />
                          {!collapsed && <span>{subItem.label}</span>}
                        </Link>
                      )
                    })}
                  </CollapsibleContent>
                </Collapsible>
              )
            }

            if (item.type === 'impostazioni') {
              return (
                <Collapsible
                  key="impostazioni"
                  open={impostazioniOpen && !collapsed}
                  onOpenChange={setImpostazioniOpen}
                >
                  <CollapsibleTrigger asChild>
                    <button
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent",
                        isImpostazioniActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={() => !collapsed && setImpostazioniOpen(!impostazioniOpen)}
                    >
                      <Cog6ToothIcon className="h-4 w-4" />
                      {!collapsed && (
                        <>
                          <span className="flex-1 text-left">Impostazioni</span>
                          <ChevronDownIcon className={cn(
                            "h-4 w-4 transition-transform",
                            impostazioniOpen && "rotate-180"
                          )} />
                        </>
                      )}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-1 pt-1">
                    {impostazioniItems.map((subItem) => {
                      const isSubActive = location.pathname === subItem.path || 
                                          location.pathname.startsWith(subItem.path + '/')
                      const SubIcon = subItem.icon
                      return (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={() => setMobileOpen(false)}
                          className={cn(
                            "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent ml-4",
                            isSubActive
                              ? "bg-primary text-primary-foreground hover:bg-primary/90"
                              : "text-muted-foreground hover:text-foreground"
                          )}
                        >
                          <SubIcon className="h-4 w-4" />
                          {!collapsed && <span>{subItem.label}</span>}
                        </Link>
                      )
                    })}
                  </CollapsibleContent>
                </Collapsible>
              )
            }
            
            return <NavLink key={item.path} item={item} />
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className={cn(
        "transition-all duration-300 ease-in-out",
        collapsed ? "lg:pl-16" : "lg:pl-64"
      )}>
        {/* Header */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 lg:px-6">
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </Button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Shopify Token Status */}
          <ShopifyTokenStatus />

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 px-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-gradient-to-br from-violet-500 to-purple-600 text-white text-xs">
                    {getInitials(user)}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium">{user?.first_name || user?.username}</p>
                  <p className="text-xs text-muted-foreground">{user?.ruolo_display || user?.ruolo}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Il mio account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profilo')}>
                <UserIcon className="mr-2 h-5 w-5" />
                Profilo
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600">
                <ArrowRightOnRectangleIcon className="mr-2 h-5 w-5" />
                Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
